<template>
  <div class="likert-container">
    <div class="likert-table-scroll">
      <table class="likert-table">
        <thead>
          <tr>
            <th class="th-dimension">Dimensão</th>
            <th
              class="th-option"
              v-for="opcao in opcoes"
              :key="opcao.cd_resposta"
            >
              {{ opcao.ds_resposta.toUpperCase() }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="dimensao in dimensoes" :key="dimensao.cd_pergunta">
            <td class="td-dimension">{{ dimensao.ds_pergunta }}</td>
            <td
              v-for="opcao in opcoes"
              :key="`${dimensao.cd_pergunta}-${opcao.cd_resposta}`"
              class="td-option"
              :class="{
                selected:
                  getSelected(dimensao.cd_pergunta) ===
                  String(opcao.cd_resposta)
              }"
            >
              <label
                :for="
                  getInputId(dimensao.cd_pergunta, opcao.cd_resposta, 'table')
                "
                class="likert-label"
              >
                <input
                  type="radio"
                  class="likert-radio"
                  :name="getGroupName(dimensao.cd_pergunta, 'table')"
                  :value="opcao.cd_resposta"
                  :id="
                    getInputId(dimensao.cd_pergunta, opcao.cd_resposta, 'table')
                  "
                  :checked="
                    getSelected(dimensao.cd_pergunta) ===
                      String(opcao.cd_resposta)
                  "
                  :disabled="disabled"
                  @change="
                    selectOption(dimensao.cd_pergunta, opcao.cd_resposta)
                  "
                />
              </label>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="likert-cards">
      <div
        v-for="dimensao in dimensoes"
        :key="`card-${dimensao.cd_pergunta}`"
        class="likert-card"
      >
        <div class="likert-card-title">{{ dimensao.ds_pergunta }}</div>
        <div class="likert-card-options">
          <label
            v-for="opcao in opcoes"
            :key="`${dimensao.cd_pergunta}-${opcao.cd_resposta}`"
            class="likert-card-option"
            :for="getInputId(dimensao.cd_pergunta, opcao.cd_resposta, 'card')"
            :class="{
              active:
                getSelected(dimensao.cd_pergunta) === String(opcao.cd_resposta)
            }"
          >
            <input
              type="radio"
              class="likert-radio"
              :name="getGroupName(dimensao.cd_pergunta, 'card')"
              :value="opcao.cd_resposta"
              :id="getInputId(dimensao.cd_pergunta, opcao.cd_resposta, 'card')"
              :checked="
                getSelected(dimensao.cd_pergunta) === String(opcao.cd_resposta)
              "
              :disabled="disabled"
              @change="selectOption(dimensao.cd_pergunta, opcao.cd_resposta)"
            />
            <span class="likert-card-option-text">{{ opcao.ds_resposta }}</span>
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
export default {
  name: "RepsostaEscalaLikert",

  props: {
    pergunta: {
      type: Object,
      required: true
    },
    modelValue: {
      type: [String, Number, Array],
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    dimensoes() {
      return Array.isArray(this.pergunta?.dimensoes)
        ? this.pergunta.dimensoes
        : [];
    },

    opcoes() {
      if (this.dimensoes.length === 0) return [];
      return Array.isArray(this.dimensoes[0]?.respostas)
        ? this.dimensoes[0].respostas
        : [];
    },

    valoresInternos() {
      const map = {};

      if (this.modelValue && typeof this.modelValue === "string") {
        const parts = this.modelValue.split("|");
        for (let i = 0; i < parts.length - 1; i += 2) {
          map[parts[i]] = parts[i + 1];
        }
      }

      if (Object.keys(map).length === 0 && this.dimensoes.length > 0) {
        for (const dim of this.dimensoes) {
          if (!Array.isArray(dim.respostas)) continue;
          const selecionada = dim.respostas.find(r => r.selecionada);
          if (selecionada) {
            map[dim.cd_pergunta] = String(selecionada.cd_resposta);
          }
        }
      }

      return map;
    }
  },

  methods: {
    getGroupName(cdPergunta, tipo) {
      return `likert-${tipo}-${cdPergunta}`;
    },

    getInputId(cdPergunta, cdResposta, tipo) {
      return `likert-${tipo}-${cdPergunta}-${cdResposta}`;
    },

    getSelected(cdPergunta) {
      return this.valoresInternos[cdPergunta] || null;
    },

    selectOption(cdPergunta, cdResposta) {
      const novo = { ...this.valoresInternos };
      novo[cdPergunta] = String(cdResposta);

      const partes = [];
      for (const [pergunta, resposta] of Object.entries(novo)) {
        if (resposta !== null && resposta !== undefined && resposta !== "") {
          partes.push(pergunta, resposta);
        }
      }

      this.$emit("update:modelValue", partes.join("|"));
    }
  }
};
</script>

<style lang="scss" scoped>
.likert-container {
  width: 100%;
  margin: 5px 0 15px;
}

.likert-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.likert-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

.likert-table thead th {
  position: sticky;
  top: 0;
  background: #f4f7fb;
  z-index: 1;
}

.th-dimension {
  text-align: left;
  padding: 12px 14px;
  font-size: 12px;
  font-weight: 700;
  color: #13345a;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 2px solid #d8e1ea;
  min-width: 180px;
}

.th-option {
  min-width: 70px;
  font-size: 11px;
  color: #13345a;
  max-width: 100px;
  line-height: 1.3;
  font-weight: bold;
  padding: 12px 8px;
  text-align: center;
  word-break: break-word;
  border-bottom: 2px solid #d8e1ea;
}

.td-dimension {
  padding: 14px 14px;
  font-size: 13px;
  color: #0d2345;
  font-weight: 500;
  border-bottom: 1px solid #e4ebf1;
  background: #ffffff;
}

.td-option {
  text-align: center;
  padding: 0;
  border-bottom: 1px solid #e4ebf1;
  background: #ffffff;
  transition: background 0.15s ease;
}

.td-option:hover {
  background: #f8fafc;
}

.td-option.selected {
  background: #f0f9f2;
}

.likert-label {
  position: relative;
  margin: 0;
  width: 100%;
  height: 100%;
  min-height: 44px;
  cursor: pointer;
  align-items: center;
  display: inline-flex;
  justify-content: center;
  transition: all 0.15s ease;
}

.likert-label::before {
  content: "";
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  transition: all 0.15s ease;
  z-index: 0;
}

.likert-label:hover::before {
  background: transparent;
}

.td-option.selected .likert-label::before {
  background: transparent;
}

.likert-radio {
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  width: 20px;
  height: 20px;
  margin: 0;
  cursor: pointer;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  background: #ffffff;
  transition: all 0.15s ease;
  z-index: 1;
  box-sizing: border-box;
  display: inline-block;
}

.likert-label:hover .likert-radio {
  border-color: #8aa3b8;
}

.likert-radio:checked {
  background: #fff;
  border-color: #00923f;
  border: 5px solid #00923f;
}

.likert-radio:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.likert-table tbody tr:last-child td {
  border-bottom: none;
}

.likert-cards {
  display: none;
}

@media (max-width: 768px) {
  .likert-table-scroll {
    display: none;
  }

  .likert-cards {
    display: block;
  }

  .likert-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    margin-bottom: 12px;
    background: #ffffff;
    border: 1px solid #e4ebf1;
    border-radius: 12px;
  }

  .likert-card-title {
    font-size: 14px;
    font-weight: 600;
    color: #0d2345;
    line-height: 1.4;
  }

  .likert-card-options {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .likert-card-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    cursor: pointer;
    border-radius: 10px;
    border: 1px solid #e4ebf1;
    transition: all 0.15s ease;
  }

  .likert-card-option:hover {
    border-color: rgb(0 146 63 / 0.5);
    background-color: #f8f9fa;
  }

  .likert-card-option.active {
    border-color: #149a4e;
    background: #f2f9f5;
  }

  .likert-card-option.active .likert-card-option-text {
    color: #0b8a40;
    font-weight: 600;
  }

  .likert-card-option-text {
    font-size: 14px;
    color: #445b78;
  }
}
</style>
